import { ChangeDetectorRef, Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormArray } from '@angular/forms';
import { Store } from '@ngxs/store';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BaseSharedState } from '@shared-store/base.state';
import { BehaviorSubject, combineLatest, filter, Observable, take } from 'rxjs';
import { CreateProfileState } from 'src/app/business/intelligencemgmt-ui/store/ui-intelligencemgmt.states';
import { ProfileManagementService } from 'src/app/business/intelligencemgmt-ui/services/profile-management.service';
import { CsvUploadDialogComponent } from '@shared-components/csv-upload-dialog/csv-upload-dialog.component';
import { SelectionDialogComponent } from '../../../business/intelligencemgmt-ui/profile-managment/create-profile/details-tab/selection-dialog/selection-dialog.component';
import * as _ from 'lodash';
import { ProfileManagmentModel } from 'src/app/business/intelligencemgmt-ui/profile-managment/model/profile.model';
import { TableCodeLabelComponent } from '@shared-components/table-code-label/table-code-label.component';
import { HscodeMappingDialogComponent } from "@shared-components/hscode-mapping-dialog/hscode-mapping-dialog.component";
import { FormService } from '@shared-services/form.service';
import { PatternValidationService } from '@shared-services/pattern-validation.service';

// Represents one item from the getCommodityHsCodesByElementName API response.
// Stored per fieldCode so we can look up the correct control type when the
// user selects an element name in the trigger dropdown.
export interface ElementFieldMeta {
  fieldCode: string;       // used as the dropdown option code
  fieldName: string;       // used as the dropdown option label
  dataType: string;        // 'S' | 'N' | 'D' | 'DT' | 'B' | 'DEC'
  inputType: string;       // 'T' | 'D' | 'C'
  masterDataLabel: string | null; // ref-type-code for select/dialog inputs
}

@Component({
  selector: 'app-business-rule-form',
  templateUrl: './business-rule-form.component.html',
  styleUrls: ['./business-rule-form.component.less']
})
export class BusinessRuleFormComponent implements OnInit, OnChanges {
  @Input() businessRuleAttr: any[] = [];
  @Input() businessRuleFormArray: FormArray;
  @Input() validationService?: any;
  @Input() isRemoved: boolean = false;
  @Input() module: string = '';
  @Output() removalProcessed = new EventEmitter<void>();
  @Output() addPairIndicator = new EventEmitter<any>();

  public _rulesList: any[] = [];
  private _rulesControlData: any[] = [];
  get rulesList(): any[] { return this._rulesList; }
  set rulesList(value: any[]) { this._rulesList = value; }

  public rulesDataType$ = new BehaviorSubject<any>(null);
  public ruleForm: UntypedFormGroup;
  public currentEditIndex = -1;
  public currentAttribute: string;
  public pageIndex = 1;
  public pageSize = 10;
  public gotoPageNumber: number;
  public expressionPreview: string = '';
  public validationErrors: any = [];
  public validationMessageList: any = [];
  public isExpressionValid: boolean = false;
  public validationStatus: 'success' | 'warning' | 'error' | null = null;
  validationMessage: any;
  public expressionTokens: any[] = [];

  // ─── TRIGGER VALUE STATE ──────────────────────────────────────────────────────
  // triggerValueOptions  — the nz-select options for the main Value/Field (elementName dropdown)
  // triggerFieldMetaMap  — full API metadata keyed by fieldCode, used to resolve
  //                        the dynamic control type for pairValue_1
  public triggerValueOptions: { code: string; label: string, masterDataLabel: string | null }[] = [];
  public triggerValueLoading = false;
  public triggerValueLoadError = false;

  /** Full raw field metadata from getCommodityHsCodesByElementName, keyed by fieldCode */
  private triggerFieldMetaMap: Map<string, ElementFieldMeta> = new Map();

  /** Resolved metadata of the CURRENTLY selected element name (triggers control-type update for pairValue_1) */
  public selectedElementFieldMeta: ElementFieldMeta | null = null;
  masterDataLabel: any;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private msg: NzMessageService,
    private modalService: NzModalService,
    private profileMgtService: ProfileManagementService,
    private profileModel: ProfileManagmentModel,
    private cdr: ChangeDetectorRef,
    public formService: FormService,
    private patternValidationSvc: PatternValidationService
  ) {
    this.initRuleForm();
  }

  openBraces = [
    { label: '', code: 0 },
    { label: '(', code: 1 },
    { label: '((', code: 2 },
    { label: '(((', code: 3 },
  ];

  closeBraces = [
    { label: '', code: 0 },
    { label: ')', code: 1 },
    { label: '))', code: 2 },
    { label: ')))', code: 3 },
  ];

  pairControls = [
    {
      trigger: 'itemAdditionalList.elementCode',
      action: false,
      operator: '=',
      operatorDisabled: true,
      logicalOperatorDisabled: true,
      logicalOperator: 'AND',
      pairs: [
        {
          attributeName: 'itemDetailsList.commodityHsCode',
          action: false,
          apiTriggerAttribute: null,
          pairOperator: '=',
          pairLogicalOperatorDisabled: true,
          pairLogicalOperator: 'AND',
          pairOperatorDisabled: true
        },
        {
          attributeName: 'itemAdditionalList.elementValue',
          action: true,
          apiTriggerAttribute: 'itemAdditionalList.elementCode',
          pairOperator: '=',
          pairOperatorDisabled: false
        }
      ]
    }
  ];

  // pairOptions[i] — options for inline dropdowns in pair rows (when control type = select from API)
  public pairOptions: Array<{ options: { code: string; label: string }[]; loading: boolean; hasError?: boolean }> = [];

  // ─── LIFECYCLE ───────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isRemoved'] && changes['isRemoved'].currentValue) {
      if (this.businessRuleAttr) {
        const availableAttributeValues = this.businessRuleAttr.map((attr: any) => attr.val);
        
        // Check if form's current attributeName is still available
        const formAttr = this.formService.ruleForm.get('attributeName')?.value as string;
        if (formAttr && !availableAttributeValues.some(val => val?.toLowerCase() === formAttr?.toLowerCase())) {
          this.resetForm();
        } else if (formAttr) {
          // Check if any pair controls reference removed attributes
          const maxPairs = Math.max(...this.pairControls.map(pc => pc.pairs.length), 0);
          let shouldClear = false;
          for (let i = 0; i < maxPairs; i++) {
            const pairCtrl = this.pairControls[0]?.pairs[i];
            if (pairCtrl && !availableAttributeValues.some(val => val?.toLowerCase() === pairCtrl.attributeName?.toLowerCase())) {
              shouldClear = true;
              break;
            }
          }
          if (shouldClear) {
            this.resetForm();
          }
        }

        // Remove from rules list if it exists
        const rulesToRemove: number[] = [];
        this._rulesList.forEach((rule, index) => {
          const isAvailable = availableAttributeValues.some(val => val?.toLowerCase() === rule.attributeName?.toLowerCase());
          if (!isAvailable) {
            rulesToRemove.push(index);
            // If this is a trigger attribute, also mark its pair members for removal
            const pc = this.pairControls.find(config => config.trigger === rule.attributeName);
            if (pc) {
              pc.pairs.forEach(pair => {
                const pairIndex = this._rulesList.findIndex(r => r.attributeName === pair.attributeName);
                if (pairIndex !== -1 && !rulesToRemove.includes(pairIndex)) {
                  rulesToRemove.push(pairIndex);
                }
              });
            } else {
              // If this is a pair member, find and remove its trigger and all other pair members
              const pairConfig = this.pairControls.find(config => config.pairs.some(pair => pair.attributeName === rule.attributeName));
              if (pairConfig) {
                const triggerIndex = this._rulesList.findIndex(r => r.attributeName === pairConfig.trigger);
                if (triggerIndex !== -1 && !rulesToRemove.includes(triggerIndex)) {
                  rulesToRemove.push(triggerIndex);
                }
                pairConfig.pairs.forEach(pair => {
                  const pairIdx = this._rulesList.findIndex(r => r.attributeName === pair.attributeName);
                  if (pairIdx !== -1 && !rulesToRemove.includes(pairIdx)) {
                    rulesToRemove.push(pairIdx);
                  }
                });
              }
            }
          }
        });

        if (rulesToRemove.length > 0) {
          [...new Set(rulesToRemove)].sort((a, b) => b - a).forEach(i => {
            this._rulesList.splice(i, 1);
            this.businessRuleFormArray?.removeAt(i);
          });
          this.updateSequences();
          this.syncFormArrayWithRulesList();
          this.loadExpressionPreview();
        }

        this.cdr.detectChanges();
        this.removalProcessed.emit();
      }
    }
  }

  ngOnInit(): void {
    this.initStoreData();
    if (this.businessRuleFormArray && this.businessRuleFormArray.length > 0) {
      this._rulesList = this.businessRuleFormArray.value
        .filter((rule: any) => rule.attributeName)
        .map((rule: any) => ({ ...rule, metaData: this.normalizeMetaData(rule.metaData) })) || [];
      if (this._rulesList.length > 0) {
        this.loadExpressionPreview();
        this.restoreCascadeOnLoad();
      }
    } else {
      this._rulesList = [];
    }
    this.syncFormArrayWithRulesList();
  }

  private normalizeMetaData(metaData: any): { action: boolean; isPair: boolean; mappingId?: string; fieldName?: string, masterDataLabel?:string, chapterCodes?: string[] } {
    if (!metaData) return { action: true, isPair: false, mappingId: '', fieldName: '', masterDataLabel: '', chapterCodes: [] };
    if (typeof metaData === 'string') {
      try { metaData = JSON.parse(metaData); } catch { return { action: true, isPair: false, mappingId: '', fieldName: '', chapterCodes: [] }; }
    } 
    let action = metaData.action;
    if (action !== null && typeof action === 'object') {
      action = action.action;
    }
    return {
      action: typeof action === 'boolean' ? action : true,
      isPair: typeof metaData.isPair === 'boolean' ? metaData.isPair : false,
      mappingId: metaData.mappingId || '',
      fieldName: metaData.fieldName || '',
      masterDataLabel: metaData?.masterDataLabel || '',
      chapterCodes: metaData?.chapterCodes || []
    };
  }

  // ─── FORM INIT ───────────────────────────────────────────────────────────────

  private initRuleForm(): void {
    const group: any = {
      openBraces: [null],
      attributeName: [null, Validators.required],
      operator: [null, Validators.required],
      value: ['', Validators.required],
      closeBraces: [null],
      logicalOperator: [null, Validators.required],
      metaData: [null]
    };
    const maxPairs = Math.max(...this.pairControls.map(pc => pc.pairs.length), 0);
    for (let i = 0; i < maxPairs; i++) {
      group[`pairOpenBraces_${i}`]      = [null];
      group[`pairOperator_${i}`]        = [''];
      group[`pairValue_${i}`]           = [''];
      group[`pairCloseBraces_${i}`]     = [null];
      group[`pairLogicalOperator_${i}`] = [i === maxPairs - 1 ? '==' : 'AND'];
      group[`metaData_${i}`]            = [null]
    }
    try {
      this.formService.ruleForm = this.fb.group(group);
    } catch (error) {
      throw error;
    }
    this.setupCascadeListeners();
  }

  // ─── CASCADE LISTENERS ────────────────────────────────────────────────────────

  private setupCascadeListeners(): void {
    const maxPairs = Math.max(...this.pairControls.map(pc => pc.pairs.length), 0);

    // ── 1. attributeName change ──────────────────────────────────────────────
    this.formService.ruleForm.get('attributeName')?.valueChanges.subscribe(attributeName => {
      const pc = this.getPairConfig(attributeName);

      if (pc) {
        // Disable operator and logicalOperator for trigger rules
        const operatorCtrl = this.formService.ruleForm.get('operator');
        operatorCtrl?.setValue(pc?.operator, { emitEvent: false });
        if (pc.operatorDisabled) {
          operatorCtrl?.disable({ emitEvent: false });
        }

        const logicalOpCtrl = this.formService.ruleForm.get('logicalOperator');
        logicalOpCtrl?.setValue(pc?.logicalOperator, { emitEvent: false });
        if (pc?.pairLogicalOperatorDisabled) {
          logicalOpCtrl?.disable({ emitEvent: false });
        }

        // Ensure companion indicator is selected
        const paircontrol = _.startCase(pc.pairs[0].attributeName.split('.')[0]);
        const checkExistIndicator = this.profileModel.listofSelectedIndicators.some((indicator: any) => indicator === paircontrol);
        if (!checkExistIndicator) {
          this.addPairIndicator.emit({ isAdd: true, paircontrol: paircontrol });
          this.profileModel.listofSelectedIndicators.push(paircontrol);
          this.cdr.detectChanges();
        }
      } else {
        this.formService.ruleForm.get('operator')?.enable({ emitEvent: false });
        this.formService.ruleForm.get('logicalOperator')?.enable({ emitEvent: false });
      }

      for (let i = 0; i < maxPairs; i++) {
        const opCtrl  = this.formService.ruleForm.get(`pairOperator_${i}`);
        const valCtrl = this.formService.ruleForm.get(`pairValue_${i}`);
        const logCtrl = this.formService.ruleForm.get(`pairLogicalOperator_${i}`);

        if (pc && i < pc.pairs.length) {
          opCtrl?.setValidators([Validators.required]);
          valCtrl?.setValidators([Validators.required]);
          logCtrl?.setValidators([Validators.required]);

          const pairDef = pc.pairs[i];
          if ((pairDef as any).pairLogicalOperatorDisabled) {
            logCtrl?.setValue((pairDef as any).pairLogicalOperator ?? pc.pairs[i].pairLogicalOperator, { emitEvent: false });
            logCtrl?.disable({ emitEvent: false });
          }
        } else {
          opCtrl?.clearValidators();
          valCtrl?.clearValidators();
          logCtrl?.clearValidators();
          opCtrl?.reset('=', { emitEvent: false });
          valCtrl?.reset('', { emitEvent: false });
          logCtrl?.reset(i === maxPairs - 1 ? '==' : 'AND', { emitEvent: false });
        }
        opCtrl?.updateValueAndValidity({ emitEvent: false });
        valCtrl?.updateValueAndValidity({ emitEvent: false });
        logCtrl?.updateValueAndValidity({ emitEvent: false });
      }

      if (!pc) {
        this.pairOptions = [];
        this.triggerValueOptions = [];
        this.triggerValueLoading = false;
        this.triggerFieldMetaMap.clear();
        this.selectedElementFieldMeta = null;
      }
      this.formService.ruleForm.get('value')?.reset('', { emitEvent: false });
      for (let i = 0; i < maxPairs; i++) {
        this.formService.ruleForm.get(`pairValue_${i}`)?.reset('', { emitEvent: false });
      }
    });

    // ── 2. pairValue_0 change (HS Code selected) ─────────────────────────────
    //    → call getCommodityHsCodesByElementName(hsCode)
    //    → store full ElementFieldMeta in triggerFieldMetaMap
    //    → fill triggerValueOptions with { code: fieldCode, label: fieldName }
    this.formService.ruleForm.get('pairValue_0')?.valueChanges.subscribe(hsCodeValue => {
      const attrName = this.formService.ruleForm.get('attributeName')?.value;
      if (!this.getPairConfig(attrName)) return;

      // Reset all downstream state
      this.formService.ruleForm.get('value')?.reset('', { emitEvent: false });
      this.formService.ruleForm.get('pairValue_1')?.reset('', { emitEvent: false });
      this.pairOptions[1] = { options: [], loading: false };
      this.triggerValueOptions = [];
      this.triggerValueLoadError = false;
      this.triggerFieldMetaMap.clear();
      this.selectedElementFieldMeta = null;

      if (hsCodeValue) {
        this.triggerValueLoading = true;
        this.cdr.detectChanges();
        this.profileMgtService.getCommodityHsCodesByElementName(hsCodeValue).subscribe({
          next: (res: any) => {
            this.buildTriggerValueOptions(res);
            this.triggerValueLoading = false;
            this.triggerValueLoadError = false;
            this.cdr.detectChanges();
          },
          error: () => {
            this.triggerValueOptions = [];
            this.triggerValueLoading = false;
            this.triggerValueLoadError = true;
            this.triggerFieldMetaMap.clear();
            this.cdr.detectChanges();
          }
        });
      } else {
        this.cdr.detectChanges();
      }
    });
    
    this.formService.ruleForm.get('value')?.valueChanges.subscribe(fieldCode => {
      const attrName = this.formService.ruleForm.get('attributeName')?.value;
      const pc = this.getPairConfig(attrName);
      if (!pc) return;

      // Reset pair[1]
      this.formService.ruleForm.get('pairValue_1')?.reset('', { emitEvent: false });
      this.pairOptions[1] = { options: [], loading: false };

      // Look up the selected field's metadata
      this.selectedElementFieldMeta = fieldCode ? (this.triggerFieldMetaMap.get(fieldCode) ?? null) : null;
    });
  }

  resolveElementValueControlType(meta: ElementFieldMeta | null): string {
    if (!meta) return 'input';

    const { dataType, inputType, masterDataLabel } = meta;

    switch (inputType) {
      case 'T':
        // Free text — control driven by dataType
        return this.resolveByDataType(dataType);

      case 'C':
        // Calendar — control driven by dataType
        return this.resolveByDataType(dataType);

      case 'D':
        // Dropdown
        if (masterDataLabel) {
          // Has a ref-type-code → open SelectionDialog with refTypeCode
          return 'select';
        }
        // No ref-type-code → inline dropdown loaded from getElementValuesByName API
        return 'apiSelect';

      default:
        return 'input';
    }
  }

  private resolveByDataType(dataType: string): string {
    switch (dataType) {
      case 'S':   return 'input';
      case 'N':   return 'number';
      case 'D':   return 'date';
      case 'DT':  return 'datetime';
      case 'B':   return 'input';
      case 'DEC': return 'number';
      default:    return 'input';
    }
  }

  /**
   * Called from the HTML to get the current control type for pairValue_1.
   * Uses selectedElementFieldMeta if available; otherwise falls back to 'input'.
   */
  getElementValueControlType(): string {
    if (!this.selectedElementFieldMeta) return 'input';
    return this.resolveElementValueControlType(this.selectedElementFieldMeta);
  }

  /**
   * Returns the refTypeCode for a select-type element value field.
   * Used when opening SelectionDialog for pairValue_1.
   */
  getElementValueRefTypeCode(): string {
    return this.selectedElementFieldMeta?.masterDataLabel ?? '';
  }

  private buildTriggerValueOptions(response: any): void {
    this.triggerValueOptions = [];
    this.triggerFieldMetaMap.clear();

    let data = response;
    if (typeof response === 'string') {
      try { data = JSON.parse(response); } catch { return; }
    }

    const items: any[] = data?.content ?? (Array.isArray(data) ? data : []);
    items.forEach((item: any) => {
      const masterDataLabel = item.masterDataLabel ?? item.refTypeCode ?? null;
      const meta: ElementFieldMeta = {
        fieldCode:       item.fieldCode ?? '',
        fieldName:       item.fieldName ?? item.fieldCode ?? '',
        dataType:        item.dataType ?? 'S',
        inputType:       masterDataLabel ? 'D' : (item.inputType ?? item.controlType ?? 'T'),
        masterDataLabel: masterDataLabel
      };
      this.triggerFieldMetaMap.set(meta.fieldCode, meta);
      this.triggerValueOptions.push({ code: meta.fieldCode, label: meta.fieldName, masterDataLabel: meta.masterDataLabel  });
    });
  }

  private initStoreData(): void {
    combineLatest([
      this.store.select(CreateProfileState.getRulesDataType),
      this.store.select(BaseSharedState.getRdmData)
    ])
      .pipe(filter(([rulesDataType]) => !!rulesDataType), take(1))
      .subscribe(([rulesDataType]) => {
        this.rulesDataType$.next(rulesDataType);
      });

    this.formService.ruleForm?.get('attributeName')?.valueChanges.subscribe(value => {
      for (let i = 0; i < this.pairControls.length; i++) {
        if (value === this.pairControls[i].trigger) {
          this.pairControls[i].pairs.forEach((pair: any) => {
            const ind = this.profileModel.listOfIndicators.find((r: any) => r.val === pair.attributeName);
            if (ind) { ind.direction = 'right'; this.cdr.detectChanges();}
          });
        }
      }
    });
  }

  // ─── RULE CRUD ───────────────────────────────────────────────────────────────

  onAddRule(): void {
    this.currentEditIndex = -1;
    this.formService.ruleForm.reset();
  }

  onEditRule(index: number, index2:any): void {
    this.rulesList.forEach(item => item.isEdit = false);
    this.currentEditIndex = index;
    this.currentAttribute = this._rulesList[index].attributeName;
    this.setControlType(this._rulesList[index].attributeName);
    const pairConfig = this.getPairConfigByAttribute(this._rulesList[index].attributeName, index);
    if (this._rulesList[index].metaData.isPair) {
      this.setupCascadeListeners();
      this.editPairRules(index, pairConfig, index2);
    } else {
      this._rulesList[index].isEdit = true;
      const ruleData = { ...this._rulesList[index] };
      ruleData.openBraces  = ruleData.openBraces  ?? 0;
      ruleData.closeBraces = ruleData.closeBraces ?? 0;
      this.formService.ruleForm.patchValue(ruleData);
      this.cdr.detectChanges();
    }
  }

  onRemoveRule(index: number): void {
    if (this.currentEditIndex === index) {
      this.msg.warning('Cannot delete rule while editing. Please save or cancel first.');
      return;
    }
    const rule = this._rulesList[index];
    const pairConfig = this.getPairConfigByAttribute(rule.attributeName, index);

    if (pairConfig) {
      this.removePairRules(index, pairConfig);
    } else {
      this._rulesList.splice(index, 1);
      this.businessRuleFormArray?.removeAt(index);
    }

    this.updateSequences();
    this.syncFormArrayWithRulesList();
    this.loadExpressionPreview();
    this.currentEditIndex = -1;
    this.msg.success('Rule removed');
  }

  onSaveRule(): void {
    if (this.formService.ruleForm.invalid) {
      this.msg.error('Please fill all required fields');
      return;
    }
    const fv = this.formService.ruleForm.getRawValue();
    let fieldName = this.triggerValueOptions.find(opt => opt.code === fv?.value)?.label;
    const pc = this.getPairConfig(fv.attributeName);
    const rule1: any = {
      openBraces:      fv.openBraces,
      attributeName:   fv.attributeName,
      operator:        fv.operator,
      value:           fv.value,
      closeBraces:     fv.closeBraces,
      logicalOperator: fv.logicalOperator,
      targetField:     this.getTargetField(fv.attributeName),
      sequence:        this.currentEditIndex === -1 ? this._rulesList.length + 1 : this.currentEditIndex + 1,
      metaData:        { action: pc ? false : true, isPair: pc ? true : false, fieldName: fieldName || null, masterDataLabel: '', chapterCodes: fv.metaData?.chapterCodes || [] },
      isEdit:          false
    };
    if (this.currentEditIndex === -1) {
      this._rulesList.push(rule1);
      if (pc) {
        pc.pairs.forEach((pair: any, i: number) => {
          const metaDataValue = fv[`metaData_${i}`] || {};
          // For elementValue pair, capture masterDataLabel from selectedElementFieldMeta
          let masterDataLabel = '';
          if (pair.attributeName === 'itemAdditionalList.elementValue' && this.selectedElementFieldMeta) {
            masterDataLabel = this.selectedElementFieldMeta.masterDataLabel || '';
          }
          this._rulesList.push({
            attributeName:   pair.attributeName,
            operator:        fv[`pairOperator_${i}`],
            value:           fv[`pairValue_${i}`],
            logicalOperator: fv[`pairLogicalOperator_${i}`],
            openBraces:      fv[`pairOpenBraces_${i}`],
            closeBraces:     fv[`pairCloseBraces_${i}`],
            targetField:     this.getTargetField(pair.attributeName),
            sequence:        this._rulesList.length + 1,
            metaData:        {action: pair.action, isPair: true, mappingId: metaDataValue?.mappingId || '', fieldName: '', masterDataLabel: masterDataLabel, chapterCodes: metaDataValue?.chapterCodes || []},
            isEdit:          false
          });
        });
      }
    } else {
      const previousRule = this._rulesList[this.currentEditIndex];
      const epc = this.getPairConfig(previousRule?.attributeName);
      
      if (epc && pc) {
        // Both previous and current are pair rules - update existing pair
        this._rulesList[this.currentEditIndex] = { ...rule1, sequence: this.currentEditIndex + 1 };
        epc.pairs.forEach((pair: any, i: number) => {
          const pr = this.findPairMemberAfterTrigger(this.currentEditIndex, pair.attributeName);
          if (pr) {
            const idx = this._rulesList.indexOf(pr);
            if (idx !== -1) {
              let fieldName = this.triggerValueOptions.find(opt => opt.code === fv[`pairValue_${i}`])?.label;
              const metaDataValue = fv[`metaData_${i}`] || {};
              // For elementValue pair, capture masterDataLabel from selectedElementFieldMeta
              let masterDataLabel = '';
              if (pair.attributeName === 'itemAdditionalList.elementValue' && this.selectedElementFieldMeta) {
                masterDataLabel = this.selectedElementFieldMeta.masterDataLabel || '';
              }
              this._rulesList[idx] = {
                attributeName:   pair.attributeName,
                operator:        fv[`pairOperator_${i}`],
                value:           fv[`pairValue_${i}`],
                logicalOperator: fv[`pairLogicalOperator_${i}`],
                openBraces:      fv[`pairOpenBraces_${i}`],
                closeBraces:     fv[`pairCloseBraces_${i}`],
                targetField:     this.getTargetField(pair.attributeName),
                sequence:        idx + 1,
                metaData:        {action: pair.action, isPair: true, mappingId: metaDataValue?.mappingId || this._rulesList[idx]?.metaData?.mappingId || '', fieldName: fieldName || null, masterDataLabel: masterDataLabel, chapterCodes: metaDataValue?.chapterCodes || this._rulesList[idx]?.metaData?.chapterCodes || []},
                isEdit:          false
              };
            }
          }
        });
      } else if (epc && !pc) {
        // Previous was pair, current is not - remove pair members
        this._rulesList[this.currentEditIndex] = rule1;
        const rulesToRemove: number[] = [];
        epc.pairs.forEach((pair: any) => {
          for (let i = this.currentEditIndex + 1; i < this._rulesList.length; i++) {
            if (this._rulesList[i].attributeName === pair.attributeName) {
              rulesToRemove.push(i);
              break;
            }
            if (this.pairControls.some(pc => pc.trigger === this._rulesList[i].attributeName)) {
              break;
            }
          }
        });
        [...new Set(rulesToRemove)].sort((a, b) => b - a).forEach(i => {
          this._rulesList.splice(i, 1);
          this.businessRuleFormArray?.removeAt(i);
        });
      } else if (!epc && pc) {
        // Previous was not pair, current is pair - add pair members
        this._rulesList[this.currentEditIndex] = { ...rule1, sequence: this.currentEditIndex + 1 };
        pc.pairs.forEach((pair: any, i: number) => {
          const metaDataValue = fv[`metaData_${i}`] || {};
          // For elementValue pair, capture masterDataLabel from selectedElementFieldMeta
          let masterDataLabel = '';
          if (pair.attributeName === 'itemAdditionalList.elementValue' && this.selectedElementFieldMeta) {
            masterDataLabel = this.selectedElementFieldMeta.masterDataLabel || '';
          }
          this._rulesList.splice(this.currentEditIndex + i + 1, 0, {
            attributeName:   pair.attributeName,
            operator:        fv[`pairOperator_${i}`],
            value:           fv[`pairValue_${i}`],
            logicalOperator: fv[`pairLogicalOperator_${i}`],
            openBraces:      fv[`pairOpenBraces_${i}`],
            closeBraces:     fv[`pairCloseBraces_${i}`],
            targetField:     this.getTargetField(pair.attributeName),
            sequence:        this.currentEditIndex + i + 2,
            metaData:        {action: pair.action, isPair: true, mappingId: metaDataValue?.mappingId || '', fieldName: '', masterDataLabel: masterDataLabel, chapterCodes: metaDataValue?.chapterCodes || []},
            isEdit:          false
          });
        });
      } else {
        // Both are non-pair rules - simple update
        this._rulesList[this.currentEditIndex] = rule1;
      }
    }

    this.updateSequences();
    this.syncFormArrayWithRulesList();
    this.loadExpressionPreview();
    this.resetForm();
    this.cdr.detectChanges();
    this.msg.success('Rule added or updated successfully!');
  }

  // ─── FORM HELPERS ────────────────────────────────────────────────────────────

  public syncFormArrayWithRulesList(): void {
    if (!this.businessRuleFormArray) {
      return;
    }
    while (this.businessRuleFormArray.length !== 0) this.businessRuleFormArray.removeAt(0);
    if (this._rulesList.length) {
      this._rulesList?.forEach((rule, index) => {
        try {
          const formControlConfig: any = {
            sequence: [rule.sequence],
            attributeName: [rule.attributeName],
            operator: [rule.operator],
            value: [rule.value],
            logicalOperator: [rule.logicalOperator],
            openBraces:  [rule.openBraces],
            closeBraces: [rule.closeBraces],
            targetField: [rule.targetField],
            metaData:    [this.normalizeMetaData(rule.metaData)]
          };
          this.businessRuleFormArray.push(this.fb.group(formControlConfig));
        } catch (error) {
          throw error;
        }
      });
    }
  }

  private updateSequences(): void {
    this._rulesList.forEach((rule, index) => { rule.sequence = index + 1; });
    if (this.businessRuleFormArray) {
      this.businessRuleFormArray.controls.forEach((ctrl, index) => ctrl.patchValue({ sequence: index + 1 }));
    }
  }

  private getTargetField(attributeName: string): string {
    const attr = _.find(this.businessRuleAttr, ['val', attributeName]);
    return attr?.indicatorName || attributeName;
  }

  private resetForm(): void {
    this.formService.ruleForm.reset();
    this.currentEditIndex = -1;
    this.pairOptions = [];
    this.triggerValueOptions = [];
    this.triggerValueLoading = false;
    this.triggerValueLoadError = false;
    this.triggerFieldMetaMap.clear();
    this.selectedElementFieldMeta = null;
  }

  onCancelRule(): void {
    this.rulesList.forEach(item => item.isEdit = false);
    this.formService.ruleForm.reset();
    this.currentEditIndex = -1;
    this.pairOptions = [];
    this.triggerValueOptions = [];
    this.triggerValueLoading = false;
    this.triggerValueLoadError = false;
    this.triggerFieldMetaMap.clear();
    this.selectedElementFieldMeta = null;
  }

  onAttributeChange(value: any): void {
    this.currentAttribute = value;
    const attr = _.find(this.businessRuleAttr, ['val', value]);
    const resolved = this.patternValidationSvc.resolveAttributePattern(attr);
    const valueCtrl = this.formService.ruleForm.get('value');
    if (resolved) {
      valueCtrl?.setValidators([Validators.required, Validators.pattern(resolved.pattern)]);
    } else {
      valueCtrl?.setValidators([Validators.required]);
    }
    valueCtrl?.updateValueAndValidity();
  }

  onOperatorChange(value: any): void {
    const valueControl = this.formService.ruleForm.get('value');
    if (value?.toLowerCase() === 'empty') {
      valueControl?.clearValidators();
      valueControl?.setValue('');
    } else {
      valueControl?.setValidators([Validators.required]);
    }
    valueControl?.updateValueAndValidity();
  }

  checkFormHasError(control: any): boolean {
    return control?.hasError('required')
      ? control?.hasError('required') && (control?.dirty || control?.touched)
      : false;
  }

  shouldShowCsvUpload(value: any): boolean {
    const operatorValue = typeof value === 'string' ? value : value?.value;
    return ['in', 'notin', 'contains'].includes(operatorValue?.toLowerCase());
  }

  openCsvUpload(): void {
    const modal = this.modalService.create({
      nzTitle: 'Upload CSV File',
      nzContent: CsvUploadDialogComponent,
      nzWidth: 500,
      nzFooter: null,
      nzData: { fileType: 'csv' }
    });
    modal.afterClose.subscribe((result: any) => { if (result) this.uploadCsvFile(result); });
  }

  private uploadCsvFile(file: any): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const values = e.target.result.split('\n')
        .filter((line: string) => line.trim())
        .flatMap((line: string) => line.split(',').map((val: string) => val.trim()));
      this.formService.ruleForm.patchValue({ value: values.join(',') });
    };
    reader.readAsText(file);
    this.msg.success('CSV file uploaded successfully');
  }

  openSelectionDialog(): void {
    const operator = this.formService.ruleForm.get('operator')?.value;
    const currentValue = this.formService.ruleForm.get('value')?.value || '';
    const selectedValues = currentValue
      ? typeof currentValue === 'string'
        ? currentValue.split(',').map((v: string) => ({ code: v.trim(), label: v.trim() }))
        : Array.isArray(currentValue) ? currentValue.map((v: string) => ({ code: v.trim(), label: v.trim() })) : []
      : [];
    const controlData = this.getControlData(this.formService.ruleForm.get('attributeName')?.value);
    const existingMeta = this.formService.ruleForm.get('metaData')?.value || {};
    const modal = this.modalService.create({
      nzTitle: 'Select Options',
      nzContent: SelectionDialogComponent,
      nzWidth: 800,
      nzData: { isMultiple: operator !== '=', selectedValues, refTypeCode: controlData?.refTypeCode, controlType: controlData?.controlType, module: this.module, selectedChapterCodes: existingMeta.chapterCodes || [] },
      nzFooter: null
    });
    modal.afterClose.subscribe((result: any) => {
      if (result) {
        this.formService.ruleForm.patchValue({ value: result.codes });
        const metaCtrl = this.formService.ruleForm.get('metaData');
        const currentMeta = metaCtrl?.value || {};
        metaCtrl?.setValue({ ...currentMeta, chapterCodes: result.chapterCodes || [] });
      }
    });
  }

  /**
   * Opens SelectionDialog for pairValue_1 when the element value field is 'select' type.
   * Uses masterDataLabel from selectedElementFieldMeta as the refTypeCode.
   */
  openElementValueSelectionDialog(pairIndex: number): void {
    const ctrlKey = `pairValue_${pairIndex}`;
    const operator = this.formService.ruleForm.get(`pairOperator_${pairIndex}`)?.value;
    const cur = this.formService.ruleForm.get(ctrlKey)?.value || '';
    const selectedValues = cur
      ? typeof cur === 'string'
        ? cur.split(',').map((v: string) => ({ code: v.trim(), label: v.trim() }))
        : Array.isArray(cur) ? cur.map((v: string) => ({ code: v.trim(), label: v.trim() })) : []
      : [];

    const refTypeCode = this.getElementValueRefTypeCode();
    const modal = this.modalService.create({
      nzTitle: 'Select Element Value',
      nzContent: SelectionDialogComponent,
      nzWidth: 800,
      nzData: {
        isMultiple: operator !== '=',
        selectedValues,
        refTypeCode,
        controlType: 'select',
        module: this.module,
        disableFavouriteMode: true  // no fav list for element value
      },
      nzFooter: null
    });
    modal.afterClose.subscribe((result: any) => {
      if (result) this.formService.ruleForm.patchValue({ [ctrlKey]: result.codes });
    });
  }

  private getControlData(attributeName: string): any {
    const parentValue = _.find(this.businessRuleAttr, ['val', attributeName]);
    if (!parentValue) return null;
    return { controlType: this.determineControlType(parentValue), refTypeCode: parentValue.refTypeCode };
  }

  private determineControlType(parentValue: any): string {
    if (parentValue.refTypeCode) return 'select';
    if (parentValue.targetField === 'hsCode' || parentValue.targetField === 'commodityHsCode') return 'hsCode';
    if (parentValue?.type?.toLowerCase()?.replace(/\s+/g, '') === 'localdate') return 'date';
    if (parentValue?.type?.toLowerCase()?.replace(/\s+/g, '') === 'localdatetime') return 'datetime';
    if (parentValue?.type?.toLowerCase() === 'boolean') return 'input';
    // If metaData.pattern present → use text input so HTML pattern validation works
    if (this.patternValidationSvc.hasPattern(parentValue)) return 'input';
    if (['integer', 'bigdecimal', 'long', 'double', 'float'].includes(parentValue?.type?.toLowerCase()?.replace(/\s+/g, ''))) return 'number';
    return 'input';
  }

  getOperatorOptions(): any[] {
    const rulesData = this.rulesDataType$.value;
    if (!rulesData) return [];
    const attributeName = this.formService.ruleForm.get('attributeName')?.value;
    const parentValue = _.find(this.businessRuleAttr, ['val', attributeName]);
    if (!parentValue) return [];
    return rulesData[this.getOperatorType(parentValue.type)] || [];
  }

  /**
   * Returns operator options for pairValue_1 based on the resolved dataType
   * from selectedElementFieldMeta. Falls back to string operators.
   */
  getElementValueOperatorOptions(): any[] {
    const rulesData = this.rulesDataType$.value;
    if (!rulesData) return rulesData?.['string'] || [];
    if (!this.selectedElementFieldMeta) return rulesData['string'] || [];
    const opType = this.resolveOperatorTypeFromDataType(this.selectedElementFieldMeta.dataType);
    return rulesData[opType] || rulesData['string'] || [];
  }

  private resolveOperatorTypeFromDataType(dataType: string): string {
    switch (dataType) {
      case 'N':   return 'numeric';
      case 'DEC': return 'numeric';
      case 'D':   return 'date';
      case 'DT':  return 'date';
      case 'B':   return 'boolean';
      case 'S':
      default:    return 'string';
    }
  }

  getAvailableAttributes(): any[] { return this.businessRuleAttr; }

  getControlType(): string {
    const attributeName = this.formService.ruleForm.get('attributeName')?.value;
    if (this.getPairConfig(attributeName)) {
      if (this.triggerValueLoadError || (!this.triggerValueLoading && this.triggerValueOptions.length === 0)) {
        return 'input';
      }
      return 'triggerSelect';
    }
    return this.getControlData(attributeName)?.controlType || 'input';
  }

  isRulesValid(): boolean {
    if (!this._rulesList?.length) return false;
    const rulesValid = this._rulesList.every((rule, index) => {
      const isLastRule = index === this._rulesList.length - 1;
      const hasRequired = rule.attributeName && rule.operator &&
        rule.value !== null && rule.value !== undefined && rule.value !== '';
      return isLastRule
        ? hasRequired && rule.logicalOperator === '=='
        : hasRequired && (rule.logicalOperator === 'AND' || rule.logicalOperator === 'OR');
    });
    return rulesValid && this.isExpressionValid;
  }

  private getOperatorType(attributeType: string): string {
    const normalizedType = attributeType?.toLowerCase().replace(/\s+/g, '');
    if (['integer', 'double', 'float', 'number', 'long', 'bigdecimal', 'big decimal'].includes(normalizedType)) return 'numeric';
    if (normalizedType === 'boolean') return 'boolean';
    if (['localdate', 'localdatetime'].includes(normalizedType)) return 'date';
    return 'string';
  }

  // ─── PAIR HELPERS ────────────────────────────────────────────────────────────

  getPairConfig(attributeName: string): any {
    return this.pairControls.find(config => config.trigger === attributeName);
  }

  getPairConfigByAttribute(attributeName: string, index: number): any {
    const isTrigger = this.pairControls.some(config => config.trigger === attributeName);
    if (isTrigger) return null;
    
    // Find the pair config this attribute belongs to
    const pairConfig = this.pairControls.find(config => config.pairs.some(pair => pair.attributeName === attributeName));
    if (!pairConfig) return null;
    
    // Find the trigger that precedes this index (same pair group)
    const triggerIndex = this.findTriggerIndexForPairMember(index, pairConfig);
    return triggerIndex !== -1 ? pairConfig : null;
  }

  private findTriggerIndexForPairMember(memberIndex: number, pairConfig: any): number {
    // Search backwards from memberIndex to find the trigger of this pair group
    for (let i = memberIndex - 1; i >= 0; i--) {
      if (this._rulesList[i].attributeName === pairConfig.trigger) {
        return i;
      }
    }
    return -1;
  }

  isPairActionRule(rule: any): boolean {
    if (!rule?.attributeName) return false;
    for (const pc of this.pairControls) {
      if (pc.trigger === rule.attributeName) return false;
      const pair = pc.pairs.find(p => p.attributeName === rule.attributeName);
      if (pair) {
        return pair.action === true;
      } else {
        return false;
      }
    }
    return true;
  }

  editPairRules(index: number, pairConfig: any, index2:any): void {
    // Find the trigger that precedes this index (same pair group)
    const triggerIndex = this.findTriggerIndexForPairMember(index, pairConfig);
    if (triggerIndex === -1) return;
    const triggerRule = this._rulesList[triggerIndex];
    this.currentEditIndex = triggerIndex;
    this._rulesList[index2].isEdit = true;

    const patch: any = {
      ...triggerRule,
      openBraces:  triggerRule.openBraces  ?? 0,
      closeBraces: triggerRule.closeBraces ?? 0,
    };

    pairConfig.pairs.forEach((pair: any, i: number) => {
      // Find the pair member that follows the trigger in the same group
      const pr = this.findPairMemberAfterTrigger(triggerIndex, pair.attributeName);
      patch[`pairOperator_${i}`]        = pr?.operator        ?? '=';
      patch[`pairValue_${i}`]           = pr?.value           ?? '';
      patch[`pairOpenBraces_${i}`]      = pr?.openBraces      ?? 0;
      patch[`pairCloseBraces_${i}`]     = pr?.closeBraces     ?? 0;
      patch[`pairLogicalOperator_${i}`] = pr?.logicalOperator ?? (i === pairConfig.pairs.length - 1 ? '==' : 'AND');
      patch[`metaData_${i}`] = pr?.metaData;
    });

    this.formService.ruleForm.patchValue(patch, { emitEvent: false });
    this.cdr.detectChanges();
    this.restoreCascadeForEdit(pairConfig, triggerRule);
  }

  private findPairMemberAfterTrigger(triggerIndex: number, attributeName: string): any {
    // Find the first occurrence of this attribute after the trigger
    for (let i = triggerIndex + 1; i < this._rulesList.length; i++) {
      if (this._rulesList[i].attributeName === attributeName) {
        return this._rulesList[i];
      }
      // Stop if we hit another trigger (next pair group)
      if (this.pairControls.some(pc => pc.trigger === this._rulesList[i].attributeName)) {
        break;
      }
    }
    return null;
  }

  private restoreCascadeForEdit(pairConfig: any, triggerRule: any): void {
    const triggerIndex = this._rulesList.indexOf(triggerRule);
    const hsCodeRule = this.findPairMemberAfterTrigger(triggerIndex, 'itemDetailsList.commodityHsCode');

    if (hsCodeRule?.value) {
      this.triggerValueLoading = true;
      this.triggerValueLoadError = false;
      this.cdr.detectChanges();
      this.profileMgtService.getCommodityHsCodesByElementName(hsCodeRule.value).subscribe({
        next: (res: any) => {
          this.buildTriggerValueOptions(res);
          this.triggerValueLoading = false;
          this.triggerValueLoadError = false;

          if (triggerRule?.value) {
            this.selectedElementFieldMeta = this.triggerFieldMetaMap.get(triggerRule.value) ?? null;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.triggerValueOptions = [];
          this.triggerValueLoading = false;
          this.triggerValueLoadError = true;
          this.cdr.detectChanges();
        }
      });
    }
  }

  removePairRules(index: number, pairConfig: any): void {
    const rulesToRemove: number[] = [];
    
    // Find the trigger that precedes this rule (same pair group)
    const triggerIndex = this.findTriggerIndexForPairMember(index, pairConfig);
    
    if (triggerIndex !== -1) {
      rulesToRemove.push(triggerIndex);
      // Remove all pair members that follow this trigger until the next trigger
      pairConfig.pairs.forEach((pair: any) => {
        for (let i = triggerIndex + 1; i < this._rulesList.length; i++) {
          if (this._rulesList[i].attributeName === pair.attributeName) {
            rulesToRemove.push(i);
            break;
          }
          // Stop if we hit another trigger (next pair group)
          if (this.pairControls.some(pc => pc.trigger === this._rulesList[i].attributeName)) {
            break;
          }
        }
      });
    }
    
    [...new Set(rulesToRemove)].sort((a, b) => b - a).forEach(i => {
      this._rulesList.splice(i, 1);
      this.businessRuleFormArray?.removeAt(i);
    });
  }

  getPairOperatorOptions(attributeName: string): any[] {
    const rulesData = this.rulesDataType$.value;
    if (!rulesData) return [];
    const parentValue = _.find(this.businessRuleAttr, ['val', attributeName]);
    if (!parentValue) return [];
    return rulesData[this.getOperatorType(parentValue.type)] || [];
  }

  getPairControlType(attributeName: string): string {
    return this.getControlData(attributeName)?.controlType || 'input';
  }

  openPairSelectionDialog(attributeName: string, pairIndex: number, isPair?: boolean, hscodeMappingData?: string, previousValue?: string, previousMappingId?: string): void {
    const ctrlKey = `pairValue_${pairIndex}`;
    const operator = this.formService.ruleForm.get(`pairOperator_${pairIndex}`)?.value;
    const cur = this.formService.ruleForm.get(ctrlKey)?.value || '';
    const selectedValues = cur
      ? typeof cur === 'string'
        ? cur.split(',').map((v: string) => ({ code: v.trim(), label: v.trim() }))
        : Array.isArray(cur) ? cur.map((v: string) => ({ code: v.trim(), label: v.trim() })) : []
      : [];
    const controlData = this.getControlData(attributeName);
    const pairMeta = this.formService.ruleForm.get(`metaData_${pairIndex}`)?.value || {};
    const modal = this.modalService.create({
      nzTitle: 'Select Options',
      nzContent: SelectionDialogComponent,
      nzWidth: 800,
      nzData: { isMultiple: operator !== '=', selectedValues, refTypeCode: controlData?.refTypeCode, controlType: controlData?.controlType, isPair, hscodeMappingData, selectedChapterCodes: pairMeta.chapterCodes || [] },
      nzFooter: null
    });
    modal.afterClose.subscribe((result: any) => {
      if (result) {
        if (result.goBack) {
          // Restore previous value before going back to mapping dialog
          if (previousValue !== undefined) {
            this.formService.ruleForm.get(ctrlKey)?.setValue(previousValue, { emitEvent: false });
            const metaDataCtrl = this.formService.ruleForm.get(`metaData_${pairIndex}`);
            if (metaDataCtrl && previousMappingId !== undefined) {
              const currentMeta = metaDataCtrl.value || {};
              metaDataCtrl.patchValue({ ...currentMeta, mappingId: previousMappingId });
            }
          }
          this.openPairHscodeDialog(attributeName, pairIndex, isPair, true);
        } else {
          setTimeout(() => {
            this.formService.ruleForm.patchValue({ [ctrlKey]: result.codes });
            const metaCtrl = this.formService.ruleForm.get(`metaData_${pairIndex}`);
            const currentMeta = metaCtrl?.value || {};
            metaCtrl?.setValue({ ...currentMeta, chapterCodes: result.chapterCodes || [] });
          }, 100);
        }
      } else {
        // User cancelled — restore previous value and mappingId
        if (previousValue !== undefined) {
          this.formService.ruleForm.get(ctrlKey)?.setValue(previousValue, { emitEvent: false });
          const metaDataCtrl = this.formService.ruleForm.get(`metaData_${pairIndex}`);
          if (metaDataCtrl && previousMappingId !== undefined) {
            const currentMeta = metaDataCtrl.value || {};
            metaDataCtrl.patchValue({ ...currentMeta, mappingId: previousMappingId });
          }
        }
      }
    });
  }

  private restoreCascadeOnLoad(): void {
    // Find all trigger rules (there can be multiple pairs)
    const triggerRules = this._rulesList.filter(r => this.pairControls.some(pc => pc.trigger === r.attributeName));
    if (triggerRules.length === 0) return;

    // For each trigger rule, find its corresponding HS code and call the API
    triggerRules.forEach((triggerRule, triggerIndex) => {
      const pc = this.getPairConfig(triggerRule.attributeName);
      if (!pc) return;

      // Find the HS code that belongs to this specific pair group
      const hsCodeRule = this.findPairMemberAfterTrigger(triggerIndex, 'itemDetailsList.commodityHsCode');

      if (hsCodeRule?.value) {
        this.triggerValueLoading = true;
        this.triggerValueLoadError = false;
        this.cdr.detectChanges();
        
        this.profileMgtService.getCommodityHsCodesByElementName(hsCodeRule.value).subscribe({
          next: (res: any) => {
            this.buildTriggerValueOptions(res);
            this.triggerValueLoading = false;
            this.triggerValueLoadError = false;

            // Restore selectedElementFieldMeta from the saved trigger value
            if (triggerRule.value) {
              this.selectedElementFieldMeta = this.triggerFieldMetaMap.get(triggerRule.value) ?? null;
            }
            this.cdr.detectChanges();
          },
          error: () => {
            this.triggerValueOptions = [];
            this.triggerValueLoading = false;
            this.triggerValueLoadError = true;
            this.cdr.detectChanges();
          }
        });
      }
    });
  }

  hasPairOptions(i: number): boolean { return (this.pairOptions[i]?.options?.length ?? 0) > 0; }
  isPairLoading(i: number): boolean  { return this.pairOptions[i]?.loading ?? false; }
  getPairOptionsList(i: number): { code: string; label: string }[] { return this.pairOptions[i]?.options ?? []; }

  isPairEnabled(pc: any, i: number): boolean {
    const pair = pc.pairs[i];
    if (!pair.apiTriggerAttribute) return true;
    return !!this.formService.ruleForm.get('value')?.value;
  }

  isPairLogicalOperatorDisabled(pc: any, i: number): boolean {
    return (pc.pairs[i] as any).pairLogicalOperatorDisabled ?? false;
  }

  isLogicalOperatorDisabled(): boolean {
    const attributeName = this.formService.ruleForm.get('attributeName')?.value;
    const pc = this.getPairConfig(attributeName);
    if (!pc) return false;
    return pc.logicalOperatorDisabled ?? false;
  }

   isOperatorDisabled(): boolean {
    const attributeName = this.formService.ruleForm.get('attributeName')?.value;
    const pc = this.getPairConfig(attributeName);
    if (!pc) return false;
    return pc.operatorDisabled ?? false;
  }

  getValuePattern(attributeName: string): string | null {
    const attr = _.find(this.businessRuleAttr, ['val', attributeName]);
    return this.patternValidationSvc.resolveAttributePattern(attr)?.pattern ?? null;
  }

  getValuePatternError(attributeName: string): string {
    const attr = _.find(this.businessRuleAttr, ['val', attributeName]);
    return this.patternValidationSvc.resolveAttributePattern(attr)?.message ?? 'Invalid format';
  }

  hasValuePattern(attributeName: string): boolean {
    const attr = _.find(this.businessRuleAttr, ['val', attributeName]);
    return this.patternValidationSvc.hasPattern(attr);
  }

  isPairControlTypeInput(pairIndex: number): boolean {
    return this.pairOptions[pairIndex]?.hasError ?? false;
  }

  // ─── TABLE / PAGINATION ──────────────────────────────────────────────────────

  getPaginatedRules(): any[] {
    const start = (this.pageIndex - 1) * this.pageSize;
    return this._rulesList.slice(start, start + this.pageSize);
  }

  onPageChange(page: number): void { this.pageIndex = page; }
  onPageSizeChange(size: number): void { this.pageSize = size; this.pageIndex = 1; }
  getTotalRulesCount(): number { return this._rulesList.length; }

  goToPage(): void {
    if (this.gotoPageNumber > 0) {
      const maxPage = Math.ceil(this._rulesList.length / this.pageSize);
      if (this.gotoPageNumber <= maxPage) this.pageIndex = this.gotoPageNumber;
    }
  }

  // ─── DISPLAY HELPERS ─────────────────────────────────────────────────────────

  private setControlType(attributeName: string): void {
    const parentValue = _.find(this.businessRuleAttr, ['val', attributeName]);
    if (!parentValue) return;
    this._rulesControlData[this.currentEditIndex] = {
      controlType: this.determineControlType(parentValue),
      operatorType: this.getOperatorType(parentValue.type),
      refTypeCode: parentValue.refTypeCode,
      indicatorName: parentValue.indicatorName
    };
  }

  getBraceLabel(code: any, braceType: 'open' | 'close'): string {
    const braces = braceType === 'open' ? this.openBraces : this.closeBraces;
    return braces.find(b => b.code == code)?.label ?? '';
  }

  getRuleBySequence(sequence: number): any {
    return this._rulesList.find(rule => rule.sequence === sequence);
  }

  getTooltipContent(sequence: number): string {
    const rule = this.getRuleBySequence(sequence);
    if (!rule) return '';
    const content = `${this.formatAttributeName(rule.attributeName)} ${rule.operator} ${rule.value}`;
    return content.length > 40 ? content.substring(0, 40) + '...' : content;
  }

  getTooltipMaxWidth(): string { return (window.innerWidth - 40) + 'px'; }
  isNaN(value: any): boolean { return Number.isNaN(value); }

  formatAttributeName(attributeName: string): string {
    const parts = attributeName.split('.');
    return parts.length > 1 ? _.startCase(parts[1]) : _.startCase(attributeName);
  }

  getFullAttributeName(attributeName: string): string {
    const parts = attributeName.split('.');
    return parts.length > 1 ? _.startCase(parts[0]) : _.startCase(attributeName);
  }

  formatRuleValue(rule: any): string {
    if (!rule?.value && rule?.value !== 0) return '';
    const attr = _.find(this.businessRuleAttr, ['val', rule.attributeName]);
    const type = attr?.type?.toLowerCase()?.replace(/\s+/g, '');
    if (['localdate', 'localdatetime', 'date', 'datetime'].includes(type)) {
      this.profileMgtService.formatDateWithTime(rule.value);
    }
    if (rule?.metaData?.fieldName && rule?.value) {
      return rule.value + ' - ' + rule.metaData.fieldName;
    }
    return rule?.value || '';
  }

  isCancelDisabled(): boolean {
    return this.formService.ruleForm.pristine && this.currentEditIndex === -1 &&
      !this.formService.ruleForm.value.attributeName && !this.formService.ruleForm.value.operator && !this.formService.ruleForm.value.value;
  }

  getRefTypeCode(data: any): string {
    const attr = _.find(this.businessRuleAttr, ['val', data?.attributeName]);
    let refTypeCode = attr?.refTypeCode || '';
    if (refTypeCode) {
      return refTypeCode;
    } else if (data?.metaData?.masterDataLabel) {
      return data?.metaData?.masterDataLabel;
    } else if (attr && (attr.targetField === 'hsCode' || attr.targetField === 'commodityHsCode' || this.determineControlType(attr) === 'hsCode')) {
      return 'hscode';
    } else {
      return '';
    }
  }

  openRuleValueDialog(rule: any): void {
    // Try to find in listOfIndicators first, fallback to businessRuleAttr
    let attr = this.profileModel.listOfIndicators?.find((r: any) => r.val === rule.attributeName);
    if (!attr) {
      attr = this.businessRuleAttr?.find((r: any) => r.val === rule.attributeName);
    }
    
    // Check if this is an hsCode field
    const isHsCode = attr?.targetField === 'hsCode' || attr?.targetField === 'commodityHsCode' || this.determineControlType(attr) === 'hsCode';
    
    // Check if we have a refTypeCode or masterDataLabel to open dialog
    const refTypeCode = attr?.refTypeCode || rule?.metaData?.masterDataLabel;
    if (!refTypeCode && !isHsCode) return;
    
    const selectedValues = rule.value
      ? typeof rule.value === 'string'
        ? rule.value.split(',').map((v: string) => ({ code: v.trim(), label: v.trim() }))
        : Array.isArray(rule.value) ? rule.value.map((v: string) => ({ code: v.trim(), label: v.trim() })) : []
      : [];
    this.modalService.create({
      nzTitle: `${this.formatAttributeName(rule.attributeName)} Values`,
      nzContent: TableCodeLabelComponent,
      nzWidth: 700,
      nzData: { selectedValues, refTypeCode, controlType: isHsCode ? 'hscode' : attr?.controlType },
      nzFooter: null
    });
  }

  // ─── EXPRESSION / VALIDATION ─────────────────────────────────────────────────

  loadExpressionPreview(): void {
    if (this._rulesList.length === 0) {
      this.isExpressionValid = false;
      this.validationMessage = null;
      this.validationStatus = null;
      this.expressionTokens = [];
      this.validationMessageList = [];
      return;
    }
    const groupedRules = this.createValidationPayload(this._rulesList);
    const service = this.validationService || this.profileMgtService;
    service.validateBraces(groupedRules).subscribe({
      next: (response: any) => {
        this.expressionPreview = response.expressionPreview || '';
        this.expressionTokens = this.parseExpressionTokens(this.expressionPreview);
        this.isExpressionValid = true;
        this.validationMessageList = response.errors || [];
        if (response.errors?.length > 0) {
          this.validationMessage = response.errors[0];
          this.validationStatus = 'warning';
        } else {
          this.validationMessage = 'Validation successful';
          this.validationStatus = 'success';
          this.validationMessageList = [];
        }
      },
      error: (error: any) => {
        if (error.status === 400 && error.error) {
          this.expressionPreview = error.error.expressionPreview || '';
          this.expressionTokens = this.parseExpressionTokens(this.expressionPreview);
          this.isExpressionValid = false;
          this.validationMessageList = error.error?.errors || [];
          this.validationMessage = error.error?.errors[0] || 'Validation warning';
          this.validationStatus = 'warning';
        } else {
          this.isExpressionValid = false;
          this.validationMessage = 'Validation failed';
          this.validationStatus = 'error';
          this.expressionTokens = [];
          this.validationMessageList = [];
        }
      }
    });
  }

  private createValidationPayload(rulesList: any[]): any[] {
    return _.chain(rulesList).groupBy('targetField').map((rules, targetField) => ({
      targetFieldGroup: _.camelCase(targetField),
      businessRule: rules.map(rule => ({
        sequence: rule.sequence === 0 ? rule.sequence + 1 : rule.sequence,
        attributeName: rule.attributeName,
        operator: rule.operator,
        value: this.processValueForValidation(rule.value),
        logicalOperator: rule.logicalOperator,
        openBraces: rule.openBraces || '0',
        closeBraces: rule.closeBraces || '0',
        targetField: rule.targetField,
        ruleId: null, detailId: null, businessDetails: [], countryGroup: null,
        metaData: rule.metaData
      }))
    })).value();
  }

  private processValueForValidation(value: any): string {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join(',').replace(/^,/, '');
    return typeof value === 'string' ? value : value.toString();
  }

  private parseExpressionTokens(expression: string): any[] {
    if (!expression) return [];
    const tokens: any[] = [];
    const regex = /(\d+|\D+)/g;
    let match;
    while ((match = regex.exec(expression)) !== null) {
      const token = match[0];
      tokens.push(/^\d+$/.test(token)
        ? { value: parseInt(token), isNumber: true }
        : { value: token, isNumber: false });
    }
    return tokens;
  }

  openPairHscodeDialog(attributeName: string, pairIndex: number, isPair?: boolean, isBack?:any): void {    
    const metaData = this.formService.ruleForm.get(`metaData_${pairIndex}`)?.value || {};
    if (metaData?.mappingId && !isBack) {
      this.openPairSelectionDialog(attributeName, pairIndex, isPair, metaData);
    } else {
      const ctrlKey = `pairValue_${pairIndex}`;
      const cur = this.formService.ruleForm.get(ctrlKey)?.value || '';
      const selectedValue = cur ? typeof cur === 'string' ? cur.split(',').map((v: string) => ({ code: v.trim(), label: v.trim() })): Array.isArray(cur) ? cur.map((v: string) => ({ code: v.trim(), label: v.trim() })) : [] : [];
      const controlData = this.getControlData(attributeName);
      const modal = this.modalService.create({
        nzTitle: 'Select Options',
        nzContent: HscodeMappingDialogComponent,
        nzWidth: 700,
        nzData: { selectedValue, refTypeCode: controlData?.refTypeCode, controlType: controlData?.controlType, isPair, selectedMappingId: metaData?.mappingId },
        nzFooter: null
      });
      modal.afterClose.subscribe((result: any) => {
        if (result) {
          const ctrlKey = `pairValue_${pairIndex}`;
          const previousValue = this.formService.ruleForm.get(ctrlKey)?.value || '';
          const previousMappingId = metaData?.mappingId || '';
          const metaDataCtrl = this.formService.ruleForm.get(`metaData_${pairIndex}`);
          if (metaDataCtrl) {
            metaDataCtrl.patchValue({ ...metaData, mappingId: result.mappingId });
          }
          // Clear old HS codes so selection dialog starts fresh
          this.formService.ruleForm.get(ctrlKey)?.setValue('', { emitEvent: false });
          this.openPairSelectionDialog(attributeName, pairIndex, isPair, result, previousValue, previousMappingId);
        }
      });
    }
  }
}